import { useEffect, useMemo, useState } from "react"

import { Drawer, Flex, Tabs, Typography } from "antd"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router"
import { useNavigate } from "react-router-dom"

import ButtonComponent from "../../../../Components/Button"
import Table from "../../../../Components/Table"
import Icon from "../../../../Icon"
import utils, { dateFormat } from "../../../../Utils/utils"
import { invoicesActions } from "../../redux/invoices.slice"
import { getInvoiceHistory, getInvoicesExportHistory } from "../../redux/invoices.thunks"
import { usePageQuery } from "../../usePageQuery"
import { getAlertsColumns } from "../Table/columns"

import "./styles.less"

const { Text } = Typography

const pageSize = 10

const StatBlock = ({ label, value }) => (
  <Flex vertical={true} gap={4}>
    <Text type="secondary">{label}</Text>
    <Text strong style={{ fontSize: 20, lineHeight: 1.4 }}>
      {value}
    </Text>
  </Flex>
)

export const InvoiceDrawer = ({ userViewForAdmin }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { pathname, search } = useLocation()
  const { openInvoiceID } = usePageQuery(search)
  const [pageNumber, setPageNumber] = useState(1)

  const { invoiceDetails, invoiceHistoryFetching, invoiceHistory, saveFetching, saveResult } =
    useSelector((state) => state.invoices)

  const { count, results } = invoiceHistory

  const open = !!openInvoiceID && !!invoiceDetails

  useEffect(() => {
    if (openInvoiceID) {
      dispatch(
        getInvoiceHistory({
          id: openInvoiceID,
          page: pageNumber,
          page_size: pageSize
        })
      )
    }
  }, [openInvoiceID, saveResult, pageNumber])

  const handleCloseModal = () => {
    const params = new URLSearchParams(search)
    params.delete("open_invoice")
    const nextSearch = params.toString()
    navigate(nextSearch ? `${pathname}?${nextSearch}` : pathname, { replace: true })
    setPageNumber(1)
    dispatch(invoicesActions.invoicesSetProp({ key: "invoiceDetails", value: null }))
  }

  const handleExport = () => {
    if (userViewForAdmin) return
    dispatch(
      getInvoicesExportHistory({
        type: "history",
        id: invoiceDetails?.id || openInvoiceID
      })
    )
  }

  const getTableColumns = useMemo(() => getAlertsColumns({ t }), [])

  const items = [
    {
      key: "Alerts",
      label: "Alerts",
      children: (
        <div style={{ marginTop: 12 }}>
          <Table
            columns={getTableColumns}
            rowKey={"created"}
            loading={invoiceHistoryFetching}
            dataSource={results}
            scroll={{ x: "auto" }}
            showSizeChanger={false}
            onChange={(pagination) => {
              setPageNumber(pagination.current)
            }}
            pagination={{
              total: count,
              current: pageNumber,
              pageSize: pageSize
            }}
          />
        </div>
      )
    }
  ]

  return (
    <Drawer
      title={t("Invoice details")}
      getContainer={() => document.getElementById("main-layout")}
      width={600}
      placement="right"
      closable={false}
      onClose={handleCloseModal}
      open={!!open}
      destroyOnClose={true}
      styles={{
        content: { borderRadius: "24px" },
        header: { padding: "16px 24px", border: "none" },
        footer: { padding: "8px 24px 24px", border: "none" }
      }}
      rootClassName="drawer-like-modal"
      extra={
        <ButtonComponent
          type="link"
          shape="circle"
          onClick={handleCloseModal}
          icon={<Icon type="cross_small" style={{ color: "#7A7A83" }} />}
        />
      }
      footer={null}
    >
      <Flex vertical={true} gap={12}>
        <Flex gap={24} align="flex-start">
          <StatBlock
            label={t("Invoice status")}
            value={invoiceDetails?.status ? utils.toUpperLatter(invoiceDetails?.status) : "—"}
          />
          <StatBlock
            label={t("Invoice date")}
            value={invoiceDetails?.date ? dayjs(invoiceDetails?.date).format(dateFormat) : "—"}
          />
          <ButtonComponent
            onClick={handleExport}
            icon={<Icon type="file_download" style={{ fontSize: 20, transform: "rotate(0deg)" }} />}
            style={{ marginLeft: "auto" }}
            loading={saveFetching}
            disabled={userViewForAdmin}
          >
            {t("Export alerts list")}
          </ButtonComponent>
        </Flex>

        <Flex gap={24} wrap={true}>
          <StatBlock
            label={t("Total amount")}
            value={"$ " + invoiceDetails?.invoice_amount || "—"}
          />
          <StatBlock label={t("Total alerts")} value={count || 0} />
          <Flex vertical={true} gap={4} style={{ flex: 1 }}>
            <Text type="secondary">{t("Invoice ID")}</Text>
            <Text
              strong
              ellipsis={{ tooltip: invoiceDetails?.invoice_id }}
              style={{ maxWidth: "342px", fontSize: 20, lineHeight: 1.4 }}
            >
              {invoiceDetails?.invoice_id}
            </Text>
          </Flex>
        </Flex>

        <Tabs defaultActiveKey="Alerts" items={items} />
      </Flex>
    </Drawer>
  )
}
